import { Box, type SxProps, type Theme } from '@mui/material';

interface ViewToggleOption<T extends string> {
    id: T;
    label: string;
}

interface ViewToggleProps<T extends string> {
    options: ViewToggleOption<T>[];
    value: T | null;
    onChange: (id: T) => void;
    ariaLabel?: string;
    sx?: SxProps<Theme>;
}

const ViewToggle = <T extends string>({
    options,
    value,
    onChange,
    ariaLabel,
    sx,
}: ViewToggleProps<T>) => (
    <Box
        role="group"
        aria-label={ariaLabel}
        sx={[
            {
                display: 'inline-flex',
                justifyContent: 'center',
                p: '4px',
                gap: '4px',
                borderRadius: '999px',
                border: '1px solid #d9d4e8',
                background: '#f6f2ff',
                boxShadow: '0 2px 8px rgba(38, 16, 78, 0.08)',
            },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
    >
        {options.map((option) => {
            const active = option.id === value;
            return (
                <Box
                    key={option.id}
                    component="button"
                    type="button"
                    aria-pressed={active}
                    onClick={() => onChange(option.id)}
                    sx={{
                        border: 'none',
                        borderRadius: '999px',
                        padding: '8px 16px',
                        fontWeight: 700,
                        fontSize: 13,
                        fontFamily: 'inherit',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        background: active ? '#5a2ea6' : 'transparent',
                        color: active ? '#ffffff' : '#5a2ea6',
                        '&:focus-visible': { outline: '2px solid #5a2ea6', outlineOffset: '2px' },
                    }}
                >
                    {option.label}
                </Box>
            );
        })}
    </Box>
);

export default ViewToggle;
